// src/screens/ChargerStation.tsx
import React, { useEffect, useState } from 'react';
import {
  SafeAreaView,
  View,
  Image,
  StyleSheet,
  Platform,
  Text,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import SignInOptions from './SignInOptions';
import Header from '../components/Header';
import ChargerInfo from '../components/ChargingStation/ChargerInfo';
import ChargerDetails from '../components/ChargingStation/ChargerDetails';
import PaymentMethodWeb from '../components/ChargingStation/PaymentMethod.web';
import PaymentMethodIOS from '../components/ChargingStation/PaymentMethod.ios';
import PaymentMethodAndroid from '../components/ChargingStation/PaymentMethod.android';
import EmailSection from '../components/ChargingStation/EmailSection';
import CreateAccountSection from '../components/ChargingStation/CreateAccountSection';
import CheckoutSection from '../components/ChargingStation/CheckoutSection';
import MobilePaymentPlaceholder from '../components/ChargingStation/MobilePaymentPlaceholder';
import PricingBreakdown from '../components/ChargingStation/PricingBreakdown';
import Scheduler from './Scheduler';
import RoleService from '../services/role-service';
import PullToRefreshWrapper from '../components/common/PullToRefreshWrapper';

import { useChargingStation } from '../hooks/useChargingStation';
import type { RootStackParamList } from '../navigation/stackTypes';
import { notificationService } from '../services/notification-service';
import { getListOfFiles, downloadFilesFromServer } from '../components/common/utils/file-handler';
import { AttachmentObj } from '../models/file-handler-model';
import { UserSessionAttributes } from '../models/user-session-model';
import { useSelector } from 'react-redux';
import { RootState } from '../store';


const height =
  typeof window !== 'undefined' && window.innerHeight ? window.innerHeight : 0;

type ChargerStationNavigationProps = NativeStackScreenProps<
  RootStackParamList,
  'ChargerStation'
>;

const ChargerStation: React.FC<ChargerStationNavigationProps> = ({
  navigation,
  route,
}) => {
  // Pull in everything from our hook:
  const {
    // State
    email,
    guestEmail,
    storedEmail,
    selectedOption,
    showCreateAccount,
    myDistanceFromCharger,
    myTimeFromCharger,
    paymentData,
    paymentsDropdownOpen,
    order,
    isValidEmail,
    loadError,
    chargerInUse,
    scheduleStartTime,
    scheduleEndTime,
    selectedMaxKwh,
    scheduleSet,
    showFeeTooltipVisible,
    activeCharges,
    session,
    selectedCharger,
    stripeElements,
    setNewOrder,
    // Stripe handlers (web)
    onDropdownPressed,
    onPaymentSubmit,
    handleRemovePayment,

    // Stripe handlers (mobile)
    openPaymentSheet,

    // Checkout
    checkoutButtonDisabled,
    handleCheckoutButton,

    // Schedule
    setScheduleTimes,
    clearSchedule,

    // Tooltip
    showFeeTooltip,
    hideFeeTooltip,

    // UI handlers
    handleEmailChange,
    handleOptionSelect,
    handleCreateAccount,
    handleSignIn,

    // Refs
    scrollViewRef,
    bottomSheetModalRef,
  } = useChargingStation(navigation, route);
  

  // State for charger background image
  const [chargerBackgroundUri, setChargerBackgroundUri] = useState<string | null>(null);
  const [loadingBackground, setLoadingBackground] = useState<boolean>(false);
  const [imageResizeMode, setImageResizeMode] = useState<'cover' | 'contain'>('cover');
  const userSession: UserSessionAttributes | undefined = useSelector(
    (state: RootState) => state.user.session
  );

  // Load charger background image
  useEffect(() => {
    const loadChargerBackground = async () => {
      if (!selectedCharger?.uuid) {
        return;
      }

      setLoadingBackground(true);
      
      try {
        // Get list of files associated with this charger
        const files = await getListOfFiles(selectedCharger.uuid);
        
        if (!files || files.length === 0) {
          // console.log('No files found for charger:', selectedCharger.uuid);
          setLoadingBackground(false);
          return;
        }

        // Find the first image file (prioritize by identifier or just take first image)
        const imageFile = files.find((file: AttachmentObj) => {
          // Check if it's an image by MIME type
          const isImage = file.attachment?.type?.startsWith('image/') || 
                         file.documentname?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
          
          // Optionally, prioritize images with specific identifier
          // e.g., return isImage && file.identifier === 'charger-background';
          return isImage;
        });

        if (!imageFile) {
          console.log('No image files found for charger');
          setLoadingBackground(false);
          return;
        }

        console.log('Found charger image:', imageFile.documentname);

        if (Platform.OS === 'web') {
          // For web, we need to manually download and create a blob URL
          // Import the web-specific download function
          const { downloadFileBinaryWeb } = require('../services/file-handler-service');
          const JSZip = require('jszip');
          
          const body = {
            action: 100, // DOWNLOAD_FILE action
            docid: imageFile.documentid,
            documentname: imageFile.documentname,
            version: 'thumbnail', // Request thumbnail for faster loading
          };
          
          const response = await downloadFileBinaryWeb('filehandler/download', body);
          
          if (response.error) {
            console.error('Failed to download image:', response.error);
            return;
          }
          
          // Decompress the ZIP file
          const zip = new JSZip();
          const loadedZip = await zip.loadAsync(response.blob);
          const firstFile = Object.keys(loadedZip.files)[0];
          const unzippedBlob = await loadedZip.files[firstFile].async('blob');
          
          // Create blob URL for display
          const blobUrl = URL.createObjectURL(unzippedBlob);
          setChargerBackgroundUri(blobUrl);
          console.log('Charger background loaded (web):', blobUrl);
        } else {
          // On native, download to local file system with thumbnail version
          const downloadedUri = await downloadFilesFromServer(imageFile, undefined, 'thumbnail');
          
          if (downloadedUri) {
            setChargerBackgroundUri(downloadedUri as string);
            console.log('Charger background loaded (native):', downloadedUri);
          }
        }
      } catch (error) {
        console.error('Error loading charger background:', error);
      } finally {
        setLoadingBackground(false);
      }
    };

    loadChargerBackground();
    
    // Cleanup: revoke blob URLs on unmount (web only)
    return () => {
      if (Platform.OS === 'web' && chargerBackgroundUri && chargerBackgroundUri.startsWith('blob:')) {
        URL.revokeObjectURL(chargerBackgroundUri);
      }
    };
  }, [selectedCharger?.uuid]);

  const renderCharger = selectedCharger;

  // If no charger is selected (e.g., after logout), show loading or empty state
  if (!renderCharger || !renderCharger.uuid) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <View
          style={[
            styles.containerBgColor,
            {
              height: height - 65,
              paddingBottom: Platform.OS === 'android' ? 0 : 10,
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
            },
          ]}
        >
          {Platform.OS !== 'web' && <Header headerText="Charging Station" />}
          <ActivityIndicator size="large" color="#51B868" />
          <Text style={{ color: '#8D8E90', marginTop: 16 }}>
            Loading charger details...
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  // Toggle image resize mode
  const toggleImageResizeMode = () => {
    setImageResizeMode(prev => prev === 'cover' ? 'contain' : 'cover');
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <View
        style={[
          styles.containerBgColor,
          {
            height: height - 65,
            paddingBottom: Platform.OS === 'android' ? 0 : 10,
            flex: 1,
          },
        ]}
      >
        {Platform.OS !== 'web' && <Header headerText="Charging Station" />}

        <PullToRefreshWrapper
          pullText="Pull down to refresh charger details"
          refreshingText="Refreshing charger data..."
          onRefresh={async () => {
            // Refresh charger data - in a real implementation, you might want to
            // add a refresh function to the useChargingStation hook
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }}
        >
          {/* Charger Background Image */}
          {loadingBackground ? (
            <View
              style={[
                Platform.OS === 'web'
                  ? { aspectRatio: 1, width: '100%', height: 230 }
                  : { width: '100%', height: 300, marginBottom: 16 },
                { 
                  backgroundColor: '#1E1E1E', 
                  justifyContent: 'center', 
                  alignItems: 'center' 
                }
              ]}
            >
              <ActivityIndicator size="large" color="#51B868" />
            </View>
          ) : (
            <View style={styles.imageContainer}>
              <Image
                source={
                  chargerBackgroundUri
                    ? { uri: chargerBackgroundUri }
                    : require('../../assets/ChargerStationBg.png')
                }
                style={
                  Platform.OS === 'web'
                    ? { aspectRatio: 1, width: '100%', height: 230 }
                    : { width: '100%', height: 300, marginBottom: 16 }
                }
                resizeMode={imageResizeMode}
                alt="Charger Background Image"
              />
              
              {/* Resize Mode Toggle Button - Only show if custom image is loaded */}
              {chargerBackgroundUri && (
                <Pressable
                  style={styles.resizeButton}
                  onPress={toggleImageResizeMode}
                  hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                >
                  <View style={styles.resizeButtonShadow}>
                    <View style={styles.resizeButtonInner}>
                      <MaterialIcons 
                        name={imageResizeMode === 'cover' ? 'zoom-out-map' : 'crop-free'} 
                        size={20} 
                        color="white" 
                      />
                    </View>
                  </View>
                </Pressable>
              )}
            </View>
          )}

          {/* ChargerInfo */}
          <ChargerInfo
            renderCharger={renderCharger}
            myDistanceFromCharger={myDistanceFromCharger}
            myTimeFromCharger={myTimeFromCharger}
            activeCharges={activeCharges}
          />

          <View style={styles.line} />

          {/* ChargerDetails */}
          <ChargerDetails renderCharger={renderCharger} />

          {/* Pricing Breakdown - Show detailed pricing information 
              Uncomment the following line to show a pricing breakdown
              We are currently not using this until more users request it
              I originally developed it based on apple asking for more pricing details
          */}
          {/* {!renderCharger.isOccupied && (
            <PricingBreakdown renderCharger={renderCharger} />
          )} */}

          {/* Show "Charger In Use" message or payment components */}
          {chargerInUse ? (
            <View style={{ marginHorizontal: 16, marginVertical: 24, padding: 20, backgroundColor: '#292D33', borderRadius: 12 }}>
              <Text style={{ fontSize: 18, fontWeight: 'bold', color: '#F97777', textAlign: 'center', marginBottom: 12 }}>
                Charger Currently In Use
              </Text>
              <Text style={{ fontSize: 14, color: '#8D8E90', textAlign: 'center', lineHeight: 20 }}>
                This charger is currently in use. Please come back later or select a different charger.
              </Text>
            </View>
          ) : (
            <>
              {/* PaymentMethod (platform-specific) */}
              {Platform.OS === 'web' && (
                <PaymentMethodWeb
                  renderChargerIsFree={renderCharger.isOccupied !== 0 && renderCharger.is_enabled === true}
                  activeCharges={activeCharges}
                  order={order}
                  paymentData={paymentData}
                  paymentsDropdownOpen={paymentsDropdownOpen}
                  onDropdownPressed={onDropdownPressed}
                  handleRemovePayment={handleRemovePayment}
                  onPaymentSubmit={onPaymentSubmit}
                  stripeElements={stripeElements}
                  loadError={loadError}
                />
              )}
              {Platform.OS === 'ios' && <PaymentMethodIOS />}
              {Platform.OS === 'android' && <PaymentMethodAndroid />}

              {/* EmailSection + CreateAccount + SignIn */}
              <EmailSection
            selectedChargerIsFree={renderCharger.isOccupied !== 0}
            activeCharges={activeCharges}
            order={order}
            sessionRole={session?.role}
            storedEmail={storedEmail}
            guestEmail={guestEmail}
            emailValid={isValidEmail}
            handleEmailChange={handleEmailChange}
            selectedOption={selectedOption}
            handleOptionSelect={handleOptionSelect}
            handleCreateAccount={handleCreateAccount}
            showCreateAccount={showCreateAccount}
            handleSignIn={handleSignIn}
            setNewOrder={setNewOrder}
            orderId={order?.id}
          />

          {/* MobilePaymentPlaceholder (logic lives in hook) */}
          {Platform.OS !== 'web' && <MobilePaymentPlaceholder />}

          {/* Scheduler */}
          {!renderCharger.isOccupied &&
            renderCharger.is_enabled === true &&
            order &&
            !activeCharges &&
            // renderCharger.can_schedule &&
            (
              <>
              {renderCharger.can_schedule && userSession && !RoleService.hasRole(userSession, 'guest') && (
              <View style={{ marginHorizontal: 16 }}>
                <Text className="text-base font-normal text-center text-slate-100">
                  Do you want to schedule this charge for later?
                </Text>
                <Scheduler
                  selectedCharger={renderCharger}
                  scheduleDate={scheduleStartTime}
                  setScheduleTimes={setScheduleTimes}
                  clearSchedule={clearSchedule}
                />
              </View>
              )}

              <View style={styles.lin2} className="py-1" />

              {/* CheckoutSection */}
              <CheckoutSection
                selectedChargerIsFree={renderCharger.isOccupied !== 0 && renderCharger.is_enabled === true}
                activeCharges={activeCharges}
                order={order}
                sessionRole={session?.role}
                scheduleSet={scheduleSet}
                paymentData={paymentData}
                isValidEmail={isValidEmail}
                handleCheckoutButton={handleCheckoutButton}
                handleOpenPaymentSheet={openPaymentSheet}
                showFeeTooltipVisible={showFeeTooltipVisible}
                showFeeTooltip={showFeeTooltip}
                hideFeeTooltip={hideFeeTooltip}
              />
            </>
          )}
            </>
          )}
          
        </PullToRefreshWrapper>

        <SignInOptions
          navigation={navigation}
          bottomSheetModalRef={bottomSheetModalRef as any}
          destination={'ChargerStation'}
          orderId={order?.id}
          // setNewOrder={(newOrder) => {
          //   console.log("setNewOrder", newOrder);
          //   // We need to wire "setNewOrder" so that if SignInOptions triggers
          //   // a brand-new order, we pass it into whichever stripe hook to reinitialize.
          //   // For simplicity here, we do nothing. In real code, you'd call
          //   // the onNewOrderCreated callback on the appropriate hook.
          // }}
          setNewOrder={setNewOrder}
        />
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0B1E1D',
  },
  containerBgColor: {
    backgroundColor: '#0B1E1D',
  },
  line: Platform.select({
    web: {
      borderBottomColor: '#438b88',
      borderBottomWidth: 1,
      borderStyle: 'dashed',
      marginVertical: 10,
    },
    default: {
      borderBottomColor: '#438b88',
      borderBottomWidth: 1,
      marginVertical: 10,
    },
  }),
  lin2: {
    borderBottomColor: '#438b88',
    borderBottomWidth: 1,
    marginVertical: 10,
  },
  imageContainer: {
    position: 'relative',
    width: '100%',
  },
  resizeButton: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 10,
  },
  resizeButtonShadow: {
    backgroundColor: '#000000',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  resizeButtonInner: {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: 8,
    padding: 10,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.3)',
  },
});

export default ChargerStation;
